import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "@via/utils/helpers.tsx";
import { FC, type ReactNode } from "react";

export enum BannerType {
  Info = "info",
  Success = "success",
  Error = "error",
  Warning = "warning",
}

export enum BannerSize {
  Small = "small",
  Normal = "normal",
}

interface MessageBannerProps {
  messages: string[] | string | JSX.Element;
  type: BannerType;
  title?: string;
  size?: BannerSize;
  /** Rendered under the messages, inside the banner: a link that belongs to the whole banner rather than one line. */
  footer?: ReactNode;
}

export const MessageBanner: FC<MessageBannerProps> = ({
  messages,
  type,
  title = "",
  size = BannerSize.Normal,
  footer,
}) => {
  const isSmall = size === BannerSize.Small;

  const styles = {
    info: {
      container: "bg-indigo-50 border-l-4 border-indigo-400 text-indigo-800",
      icon: <InformationCircleIcon className={isSmall ? "h-5" : "h-6"} />,
    },
    success: {
      container: "bg-green-50 border-l-4 border-green-400 text-green-800",
      icon: <CheckCircleIcon className={isSmall ? "h-5" : "h-6"} />,
    },
    error: {
      container: "bg-red-50 border-l-4 border-red-400 text-red-800",
      icon: <ExclamationCircleIcon className={isSmall ? "h-5" : "h-6"} />,
    },
    warning: {
      container: "bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800",
      icon: <ExclamationTriangleIcon className={isSmall ? "h-5" : "h-6"} />,
    },
  };

  const selectedStyle = styles[type];

  if (Array.isArray(messages) && messages.length === 0) {
    return null;
  }

  const containerPadding = isSmall ? "p-3" : "p-4";
  const titleSize = isSmall ? "text-base" : "text-lg";
  const titleMargin = isSmall ? "mb-1.5" : "mb-2";
  const textSize = isSmall ? "text-xs" : "text-sm";
  const listSpacing = isSmall ? "space-y-0.5" : "space-y-1";

  return (
    <div
      className={classNames(
        "mx-auto mt-4 w-full",
        containerPadding,
        selectedStyle.container
      )}>
      <h3
        className={classNames(
          "flex items-center font-semibold",
          titleSize,
          titleMargin
        )}>
        {selectedStyle.icon}
        <span className="ml-2">
          {title ||
            (type === BannerType.Error
              ? "Error"
              : type === BannerType.Success
                ? "Success"
                : type === BannerType.Info
                  ? "Info"
                  : "Warning")}
        </span>
      </h3>
      {Array.isArray(messages) ? (
        <ul className={classNames("list-disc pl-5", listSpacing)}>
          {messages.map((message, index) => (
            <li key={index} className={textSize}>
              {message}
            </li>
          ))}
        </ul>
      ) : (
        <p className={classNames("flex", textSize)}>{messages}</p>
      )}
      {footer ? (
        <div className={classNames("mt-2", textSize)}>{footer}</div>
      ) : null}
    </div>
  );
};
